import type React from "react";
import { useEffect, useState, useCallback } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { useFilter } from "../../../context/FilterContext";
import { Download, ExternalLink } from "lucide-react";
import useApiHook from "../../../utils/useApi";
import type {
  ApiCustomerResponse,
  CustomerData,
} from "../../../interface/interfaces";
import { formatDateTime } from "../../../utils/utils";
import { customStyles } from "../../../utils/table_utils";

const CustomersTable: React.FC = () => {
  const { startDate, endDate, searchQuery } = useFilter();

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const { data, isLoading, makeRequest } = useApiHook<ApiCustomerResponse>(
    "/dashboard/customers",
    {
      method: "get",
    }
  );

  const exportToCSV = () => {
    if (!data?.data?.data || data.data.data.length === 0) {
      return;
    }

    setIsExporting(true);

    try {
      const filteredData = data.data.data.map((customer) => {
        const { ...filteredCustomer } = customer;
        return filteredCustomer;
      });

      const headers = Object.keys(filteredData[0]);
      const csvHeader = headers.join(",");

      const csvRows = filteredData.map((row) => {
        return headers
          .map((header) => {
            const value = row[header as keyof typeof row];

            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(",");
      });

      const csvContent = [csvHeader, ...csvRows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `customers_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns: TableColumn<CustomerData>[] = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.name}</div>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => <div>{row.email}</div>,
    },
    {
      name: "Code",
      selector: (row) => row.code,
      sortable: true,
      cell: (row) => (
        <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
          {row.code}
        </div>
      ),
    },
    {
      name: "Date Created",
      selector: (row) => row.dateCreated,
      sortable: true,
      cell: (row) => <div>{formatDateTime(row.dateCreated)}</div>,
    },
  ];

  const fetchCustomers = useCallback(async () => {
    setHasError(false);
    setErrorMessage("");

    const params = {
      page,
      size: perPage,
      search: searchQuery || "",
      startDate: startDate || "",
      endDate: endDate || "",
    };

    const response = await makeRequest(params);

    if (response?.data?.statusCode !== "00") {
      setHasError(true);
      setErrorMessage(response?.data?.description || "An error occurred");
      setTotalRows(0);
    } else if (response?.data?.data) {
      setTotalRows(response.data.data.totalCount || 0);
    }
  }, [page, perPage, searchQuery, startDate, endDate]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const LoadingComponent = () => (
    <div className="flex justify-center items-center p-8">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-red-500"></div>
    </div>
  );

  return (
    <section className="bg-gray-50 p-4 md:p-6 rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        </div>
        <button
          onClick={exportToCSV}
          disabled={isExporting || isLoading || !data?.data?.data?.length}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
          ) : (
            <Download size={18} className="mr-2" />
          )}
          Export CSV
        </button>
      </div>

      {hasError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          <p className="font-medium">Error</p>
          <p className="text-sm">{errorMessage}</p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data?.data?.data || []}
          progressPending={isLoading}
          progressComponent={<LoadingComponent />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          highlightOnHover
          responsive
          persistTableHead
          customStyles={customStyles}
          noDataComponent={
            <div className="p-6 text-center text-gray-500">
              No customers found
            </div>
          }
        />
      )}
    </section>
  );
};

export default CustomersTable;
